import { useLingui } from '@lingui/react/macro';

import { IconElement } from '@watershed/icons/Icon';
import DeleteIcon from '@watershed/icons/components/Delete';
import React from 'react';
import IconButton, { IconButtonProps } from '../../IconButton';
import ArrowNEIcon from '@watershed/icons/components/ArrowNE';
import CloseIcon from '@watershed/icons/components/Close';
import DownloadIcon from '@watershed/icons/components/Download';
import mixinSx from '@watershed/style/mixinSx';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import { UploadFile } from '../../../utils/FileUpload';
import { FileItemRow } from './FileItemRow';
import { SheetSelection } from './SheetSelection';
import { SheetWithSelection } from './SheetSelection';

export interface FileItemProps {
  file: UploadFile;
  removeUpload?: (fileId: string) => void;
  disabled: boolean;
  progress: number;
  sheets?: Array<SheetWithSelection>;
  onChangeSheetSelection?: (
    fileId: string,
    sheetSelections: Array<boolean>
  ) => void;
  onClickPreviewFile?: (file: UploadFile) => void;
  canPreviewFile?: (file: UploadFile) => boolean;
  getDownloadUrl?: (file: UploadFile) => Promise<string | undefined>;
  tablePreviewer?: (tableId: string) => React.ReactNode;
}

/**
 * Internal component used in `FileUploadDropzone`, exposing standard actions for a file item and the
 * ability to pick which sheets to import from an Excel file.
 */
export function FileItem({
  file,
  removeUpload,
  disabled,
  progress,
  sheets,
  onChangeSheetSelection,
  onClickPreviewFile,
  canPreviewFile,
  getDownloadUrl,
  tablePreviewer,
}: FileItemProps) {
  const { t } = useLingui();
  const { filename, fileId, isImported, error, uploadDate } = file;

  let statusMessage: LocalizedString;
  let statusIcon: 'success' | 'error' | 'loading';
  if (error) {
    statusIcon = 'error';
    statusMessage = error;
  } else if (isImported) {
    statusIcon = 'success';
    statusMessage = uploadDate
      ? t`Imported on ${uploadDate}`
      : t`File imported successfully`;
  } else {
    statusIcon = 'loading';
    statusMessage = progress < 100 ? t`Uploading…` : t`Processing…`;
  }

  const downloadFile = async () => {
    if (getDownloadUrl === undefined) {
      return;
    }
    const downloadUrl = await getDownloadUrl(file);
    if (downloadUrl === undefined) {
      return;
    }
    window.open(downloadUrl, '_new');
  };

  return (
    <FileItemRow
      icon={statusIcon}
      title={filename}
      subtitle={statusMessage}
      progress={progress}
      actions={
        isImported ? (
          <>
            <FileItemRowActionButton
              disabled={disabled}
              onClick={() => removeUpload?.(fileId)}
              Icon={DeleteIcon}
              color="error"
              tooltip={t({
                message: 'Delete file',
                context:
                  'Tooltip text for the delete action in a file upload item row',
              })}
            />
            {getDownloadUrl !== undefined && (
              <FileItemRowActionButton
                disabled={disabled}
                onClick={downloadFile}
                Icon={DownloadIcon}
                tooltip={t({
                  message: 'Download file',
                  context:
                    'Tooltip text for the download action in a file upload item row',
                })}
              />
            )}
            {onClickPreviewFile !== undefined &&
              (canPreviewFile ? canPreviewFile(file) : true) && (
                <FileItemRowActionButton
                  disabled={false}
                  onClick={() => onClickPreviewFile(file)}
                  Icon={ArrowNEIcon}
                  color="primary"
                  tooltip={t({
                    message: 'Preview file',
                    context:
                      'Tooltip text for the preview action in a file upload item row',
                  })}
                />
              )}
          </>
        ) : (
          // Cancel buttons aren't disabled ever, because it should be accessible any time it shows up
          <FileItemRowActionButton
            disabled={false}
            data-testid={TestIds.FileUploadMultipleCancel}
            onClick={() => removeUpload?.(fileId)}
            color="error"
            Icon={CloseIcon}
            tooltip={t({
              message: 'Cancel upload',
              context:
                'Tooltip text for the cancel upload action in a file upload item row',
            })}
          />
        )
      }
    >
      {sheets && onChangeSheetSelection && (
        <SheetSelection
          sheets={sheets}
          onChange={(selectedSheets) =>
            onChangeSheetSelection(fileId, selectedSheets)
          }
          tablePreviewer={tablePreviewer}
        />
      )}
    </FileItemRow>
  );
}

/**
 * Standard buttons for actions on a file item row.
 */
export function FileItemRowActionButton({
  Icon,
  color = 'default',
  ...props
}: {
  Icon: IconElement;
  disabled: boolean;
  tooltip: LocalizedString;
  color?: 'default' | 'primary' | 'error';
} & Omit<
  IconButtonProps,
  'color' | 'disabled' | 'size' | 'tooltip' | 'sx' | 'children'
>) {
  return (
    <IconButton
      {...props}
      color="secondary"
      size="large"
      sx={mixinSx(
        {
          p: 1,
          transition: (theme) =>
            theme.transitions.create('background-color', {
              duration: theme.transitions.duration.shorter,
            }),
          '&:hover, &:focus': {
            color: (theme) => theme.palette.text.primary,
          },
        },
        color === 'default' && {
          '&:hover': {
            backgroundColor: (theme) => theme.palette.grey10,
          },
        },
        color === 'primary' && {
          '&:hover': {
            backgroundColor: 'primary.light',
            color: 'primary.main',
          },
        },
        color === 'error' && {
          '&:hover': {
            backgroundColor: 'error.light',
            color: 'error.main',
          },
        }
      )}
    >
      <Icon size={14} />
    </IconButton>
  );
}
